import React, { FormEvent, useContext, useEffect, useRef, useState } from 'react';
import useClickStreamEvent from 'utils/clickstream/useClickStreamEvent';
import { appAuthContext } from 'context/AuthContext';
import Dropdown from 'views/Profile/components/Dropdown';
import AuthModalController from 'components/AuthModalController/AuthModalController';
import Modal from '../Modal';
import { subjectData, levelData } from './AskModal.data';
import { AskModalProps, SelectedValue } from './AskModal.types';
import postAskData from './api/sendAskData';
import styles from './AskModal.module.css';

const AskModal = ({ onClose, questionText, tryCreateNew }: AskModalProps) => {
  const { sendEvent } = useClickStreamEvent();
  const { user } = useContext(appAuthContext);
  const [questionLength, setQuestionLength] = useState<number>(0);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const incorrectQuestionLength = questionLength < 50;

  // Fields value state
  const [askValue, setAskValue] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<SelectedValue>(null);
  const [selectedLevel, setSelectedLevel] = useState<SelectedValue>(null);

  // Error State
  const [textAreaHasError, setTextAreaHasError] = useState(false);
  const [networkError, setNetworkError] = useState(false);

  // Modal Show State
  const [showRegModal, setShowRegModal] = useState<boolean>(false);
  const [showAskModal, setShowAskModal] = useState<boolean>(true);

  const userIsAuth = !user?.data?.attributes;

  // Highlighting fields state
  const [highlightTextArea, setHighlightTextArea] = useState(false);
  const [highlightSubjectArea, setHighlightSubjectArea] = useState(false);
  const [highlightGradeArea, setHighlightGradeArea] = useState(false);

  useEffect(() => {
    if (questionText) {
      return setAskValue(questionText);
    }

    // Value from localStorage
    const savedQuestion = localStorage.getItem('saved-question');
    const savedSubject = localStorage.getItem('saved-subject');
    const savedGrade = localStorage.getItem('saved-grade');
    if (savedQuestion) {
      setAskValue(savedQuestion);
      setQuestionLength(savedQuestion.length);
    }

    if (savedSubject) {
      handleSubjectChange(savedSubject);
    }

    if (savedGrade) {
      handleGradeChange(savedGrade);
    }
  }, []);

  const handleAskValueChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setHighlightTextArea(false);
    setAskValue(e.target.value);
    setQuestionLength(e.target.value.length);
    localStorage.setItem('saved-question', e.target.value);
  };

  const handleSubjectChange = (value: string) => {
    const selectedItem = subjectData.filter((i) => i.value === value);
    setSelectedSubject(selectedItem[0] || '');
    setHighlightSubjectArea(false);
    localStorage.setItem('saved-subject', value);
  };

  const handleGradeChange = (value: string) => {
    const selectedItem = levelData.filter((i) => i.value === value);
    setSelectedLevel(selectedItem[0] || '');
    setHighlightGradeArea(false);
    localStorage.setItem('saved-grade', value);
  };

  const highlightIncorrectArea = () => {
    if (incorrectQuestionLength) {
      setHighlightTextArea(true);
      setTextAreaHasError(true);
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }

    if (!selectedSubject?.value) {
      setHighlightSubjectArea(true);
    }

    if (!selectedLevel?.value) {
      setHighlightGradeArea(true);
    }
  };

  const removeValuesLocalStorage = () => {
    localStorage.removeItem('saved-question');
    localStorage.removeItem('saved-subject');
    localStorage.removeItem('saved-grade');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // setButtonDisabled(true);
    const formIsValid =
      !incorrectQuestionLength && selectedLevel?.value && selectedSubject?.value;

    if (!formIsValid) {
      return highlightIncorrectArea();
    }

    // return null;

    const res = await postAskData(
      askValue,
      +selectedSubject?.value,
      +selectedLevel?.value,
      userIsAuth,
    );
    if (res?.success && userIsAuth) {
      setShowAskModal(false);
      setShowRegModal(true);
      removeValuesLocalStorage();
      return;
    }
    if (res?.success && res?.questionPath) {
      await sendEvent({
        eventType: 'click',
        eventInfo: 'otvety_new_question_create',
      });
      window.location.href = `/otvety/questions/${res?.questionPath}`;
    }
    if (res && !res.errors_text) {
      removeValuesLocalStorage();
      onClose();
    } else {
      setNetworkError(true);
    }
    setButtonDisabled(false);
  };

  return (
    <>
      {showAskModal && (
        <Modal
          onClose={onClose}
          allowCloseOnClickOutside={false}
          className={styles.modal_wrapper}
        >
          <div className={styles.container}>
            <div className={styles.ask_container}>
              <h2 className={styles.ask_title}>Задать вопрос на&nbsp;Учи.Ответах</h2>
              <form id="new_question" className={styles.ask_form} noValidate>
                <div className={styles.ask_content}>
                  <textarea
                    name="question"
                    className={`${styles.ask_textarea} ${
                      highlightTextArea ? styles.field_err : ''
                    }`}
                    value={askValue}
                    onChange={handleAskValueChange}
                    data-qa-marker="new_question_textarea"
                    maxLength={10000}
                    placeholder="Напиши его здесь"
                    ref={textareaRef}
                  />
                  <div className={styles.hints}>
                    <div
                      className={`${styles.hint} ${
                        incorrectQuestionLength ? styles.hint__error : ''
                      } ${!textAreaHasError ? styles.hint__hide : ''}`}
                    >
                      <div className={styles.hint__status} />
                      <span className={styles.hint__text}>
                        Вопрос должен быть не короче 50 символов ({questionLength}/50)
                      </span>
                    </div>
                    <div
                      className={`${styles.hint} ${
                        incorrectQuestionLength && textAreaHasError
                          ? styles.hint_transparent
                          : ''
                      }`}
                    >
                      <div className={styles.hint__status} />
                      <span className={styles.hint__text}>
                        Сразу переходи к&nbsp;главному, не&nbsp;отвлекайся
                        на&nbsp;приветствия, эмоции и&nbsp;второстепенные вещи
                      </span>
                    </div>
                    <div
                      className={`${styles.hint} ${
                        incorrectQuestionLength && textAreaHasError
                          ? styles.hint_transparent
                          : ''
                      }`}
                    >
                      <div className={styles.hint__status} />
                      <span className={styles.hint__text}>
                        Не&nbsp;используй мат и&nbsp;оскорбления&nbsp;&mdash; такие
                        вопросы мы&nbsp;не&nbsp;публикуем
                      </span>
                    </div>
                  </div>
                </div>
                <div className={styles.ask_group}>
                  <div className={styles.dropdowns_container}>
                    <div className={styles.ask_panel}>
                      <p className={styles.ask_dropdown_label}>Предмет</p>
                      <Dropdown
                        className={`${styles.dropdown} ${
                          highlightSubjectArea ? styles.field_err : null
                        }`}
                        placeholder="Выбери предмет"
                        options={subjectData}
                        value={selectedSubject?.value || ''}
                        onChange={(value) => handleSubjectChange(value)}
                      />
                    </div>
                    <div className={styles.ask_panel}>
                      <p className={styles.ask_dropdown_label}>Класс вопроса</p>
                      <Dropdown
                        className={`${styles.dropdown} ${
                          highlightGradeArea ? styles.field_err : null
                        }`}
                        placeholder="Выбери класс"
                        options={levelData}
                        value={selectedLevel?.value || ''}
                        onChange={(value) => handleGradeChange(value)}
                      />
                    </div>
                  </div>
                  <div className={`${styles.hints} ${styles.hints__dropdown_group}`}>
                    <div
                      className={`${styles.hint} ${
                        highlightSubjectArea || highlightGradeArea
                          ? styles.hint__error
                          : null
                      }`}
                    >
                      <div className={styles.hint__status} />
                      <span className={styles.hint__text}>
                        Эти данные помогут быстрее получить ответ
                      </span>
                    </div>
                  </div>
                </div>
                <p
                  className={`${styles.ntw_err} ${
                    !networkError ? styles.ntw_err__hide : ''
                  }`}
                >
                  Произошла ошибка, повторите попытку позже
                </p>
                <div className={styles.ask_button_container}>
                  <button
                    onClick={handleSubmit}
                    className={styles.ask_button}
                    disabled={buttonDisabled}
                    data-qa-marker="new_question_send_btn"
                  >
                    Задать вопрос
                  </button>
                  <button
                    onClick={onClose}
                    className={styles.close_button}
                    disabled={buttonDisabled}
                    data-qa-marker="new_question_close_btn"
                  >
                    <div className={styles.close_button__cross} />
                    <span className={styles.close_button__text}>Отмена</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Modal>
      )}
      {showRegModal && (
        <AuthModalController
          handleClose={() => {
            setShowRegModal(false);
          }}
          entryPoint={tryCreateNew}
        />
      )}
    </>
  );
};

export default AskModal;
